import { faArrowRight, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { RadioCard, Tooltip } from "@intility/bifrost-react";
import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import Grid from "@intility/bifrost-react/Grid";
import Icon from "@intility/bifrost-react/Icon";
import Input from "@intility/bifrost-react/Input";
import Select from "@intility/bifrost-react-select";
import { useEffect, useState } from "react";
import { useBlueprintAccess } from "~/api/blueprintAccess";
import type { CreateResourceTypeRequest, ResourceType } from "~/api/fga-types";
import { slugify } from "~/lib/clientTypes";
import {
  composeDynamicRegex,
  deriveSegmentTemplate,
  ID_PATTERNS,
  type IdPatternKey,
  isValidPlaceholder,
  literalValue,
} from "~/lib/resourceType";
import type { BlueprintSelection } from "./BlueprintCanvas";
import { LinkedPlatforms } from "./LinkedPlatforms";

function suggestQualifiedName(
  type: ResourceType,
  parentQualifiedName: string | null,
): string {
  const slug = type.slug;
  const segment = type.isStatic
    ? slug
    : slug.endsWith("s") && slug.length > 1
      ? slug.slice(0, -1)
      : slug;
  return parentQualifiedName ? `${parentQualifiedName}.${segment}` : segment;
}

export interface BlueprintDrawerProps {
  selection: BlueprintSelection | null;
  availableTypes: ResourceType[];
  busy: boolean;
  onAddUsage: (
    platformSlug: string,
    parentUsageId: string | null,
    typeId: string,
    qualifiedName: string,
  ) => Promise<void> | void;
  onUpdateUsage: (
    usageId: string,
    qualifiedName: string,
  ) => Promise<void> | void;
  onDeleteUsage: (usageId: string) => Promise<void> | void;
  onCreateType: (
    req: CreateResourceTypeRequest,
  ) => Promise<ResourceType | undefined>;
  /**
   * Prechecks a placement for a not-yet-created type (the "create new type"
   * flow) before onCreateType runs, so a placement that would be rejected
   * never leaves an orphan type in the catalog. Rejects with the failure
   * reason; the host is expected to also surface it via its own error state.
   */
  onValidatePlacement: (
    platformSlug: string,
    parentUsageId: string | null,
    qualifiedName: string,
    typeNameRegex: string | undefined,
    typeIsStatic: boolean,
  ) => Promise<void>;
  onShowGuide?: () => void;
  /**
   * Which sections to show. "inspect" = view/edit/delete only; "add" = the
   * add-child form only. Undefined shows both (legacy viewer behaviour).
   */
  mode?: "inspect" | "add";
}

type AddMode = "search" | "create";

export default function BlueprintDrawer({
  selection,
  availableTypes,
  busy,
  onAddUsage,
  onUpdateUsage,
  onDeleteUsage,
  onCreateType,
  onValidatePlacement,
  onShowGuide: _onShowGuide,
  mode,
}: BlueprintDrawerProps) {
  const showInspect = mode !== "add";
  const showAdd = mode !== "inspect";
  const platformShort =
    selection && selection.kind !== "company"
      ? selection.platformSlug.split("/").pop()
      : undefined;
  const { canEdit: canEditBlueprint } = useBlueprintAccess(platformShort);

  const [addMode, setAddMode] = useState<AddMode>("search");
  const [typeId, setTypeId] = useState("");
  const [qualifiedName, setQualifiedName] = useState("");
  const [qnTouched, setQnTouched] = useState(false);

  const [newSlug, setNewSlug] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newSlugTouched, setNewSlugTouched] = useState(false);
  const [newCardinality, setNewCardinality] = useState<"single" | "many">(
    "single",
  );
  const [newPrefix, setNewPrefix] = useState("");
  const [newPlaceholder, setNewPlaceholder] = useState("");
  const [newIdPattern, setNewIdPattern] = useState<IdPatternKey>("guid");
  const [newCustomPattern, setNewCustomPattern] = useState("");

  const [editQualifiedName, setEditQualifiedName] = useState("");

  const selectedTypeObj = typeId
    ? (availableTypes.find((t) => t.id === typeId) ?? null)
    : null;

  const parentQualifiedName =
    selection?.kind === "resource" ? selection.qualifiedName : null;

  const selectionQn =
    selection?.kind === "resource" ? selection.qualifiedName : null;

  useEffect(() => {
    setAddMode("search");
    setTypeId("");
    setQualifiedName("");
    setQnTouched(false);
    setNewSlug("");
    setNewDisplayName("");
    setNewSlugTouched(false);
    setNewCardinality("single");
    setNewPrefix("");
    setNewPlaceholder("");
    setNewIdPattern("guid");
    setNewCustomPattern("");
    setEditQualifiedName(selectionQn ?? "");
  }, [selectionQn]);

  useEffect(() => {
    if (addMode === "search" && selectedTypeObj && !qnTouched) {
      setQualifiedName(
        suggestQualifiedName(selectedTypeObj, parentQualifiedName),
      );
    }
  }, [selectedTypeObj, parentQualifiedName, addMode, qnTouched]);

  useEffect(() => {
    if (addMode === "create" && !qnTouched) {
      const slug = newSlug.trim();
      if (!slug) {
        setQualifiedName("");
        return;
      }
      const segment =
        newCardinality === "many" && slug.endsWith("s") && slug.length > 1
          ? slug.slice(0, -1)
          : slug;
      setQualifiedName(
        parentQualifiedName ? `${parentQualifiedName}.${segment}` : segment,
      );
    }
  }, [newSlug, newCardinality, parentQualifiedName, addMode, qnTouched]);

  const typeOptions = availableTypes.map((t) => ({
    value: t.id,
    label: `${t.displayName} (${t.slug})`,
  }));

  const selectedType = typeId
    ? (typeOptions.find((o) => o.value === typeId) ?? null)
    : null;

  const newBuilderPattern =
    newIdPattern === "custom"
      ? newCustomPattern.trim()
      : ID_PATTERNS[newIdPattern].pattern;

  const generatedRegex =
    addMode === "create"
      ? newCardinality === "single"
        ? `^${newSlug.trim()}$`
        : newPlaceholder.trim()
          ? composeDynamicRegex(
              newPrefix.trim(),
              newPlaceholder.trim(),
              newBuilderPattern,
            )
          : null
      : null;

  const newPreviewSegment = generatedRegex
    ? deriveSegmentTemplate(generatedRegex)
    : null;

  const canAddFromCatalog = !busy && typeId && qualifiedName.trim();

  const canAddFromCreate =
    !busy &&
    newSlug.trim() &&
    newDisplayName.trim() &&
    qualifiedName.trim() &&
    (newCardinality === "single" ||
      (newPlaceholder.trim() &&
        isValidPlaceholder(newPlaceholder.trim()) &&
        newBuilderPattern.length > 0));

  const handleAddFromCatalog = async () => {
    if (!selection || selection.kind === "company") return;
    await onAddUsage(
      selection.platformSlug,
      selection.kind === "resource" ? selection.usageId : null,
      typeId,
      qualifiedName.trim(),
    );
    setTypeId("");
    setQualifiedName("");
    setQnTouched(false);
  };

  const handleAddFromCreate = async () => {
    if (!selection || selection.kind === "company") return;
    const parentUsageId =
      selection.kind === "resource" ? selection.usageId : null;
    try {
      await onValidatePlacement(
        selection.platformSlug,
        parentUsageId,
        qualifiedName.trim(),
        generatedRegex ?? undefined,
        newCardinality === "single",
      );
    } catch {
      // The host already surfaces the failure via its own error state; the
      // type is never created, so nothing needs rolling back.
      return;
    }
    const req: CreateResourceTypeRequest = {
      slug: newSlug.trim(),
      displayName: newDisplayName.trim(),
      nameRegex: generatedRegex ?? undefined,
      isStatic: newCardinality === "single",
    };
    const newType = await onCreateType(req);
    if (!newType) return;
    await onAddUsage(
      selection.platformSlug,
      parentUsageId,
      newType.id,
      qualifiedName.trim(),
    );
    setNewSlug("");
    setNewDisplayName("");
    setNewSlugTouched(false);
    setNewCardinality("single");
    setNewPrefix("");
    setNewPlaceholder("");
    setNewIdPattern("guid");
    setNewCustomPattern("");
    setQualifiedName("");
    setQnTouched(false);
  };

  const noSelection = !selection;

  return (
    <div className="bp-drawer-body">
      {noSelection && (
        <p
          className="bf-p bf-small"
          style={{ color: "var(--bfc-base-c-2)", margin: 0 }}
        >
          Select a node on the canvas to add resources.
        </p>
      )}

      {selection?.kind === "company" && (
        // A blueprint exists when its Platform exists (ADR-0059), so there is
        // nothing to add here.
        <p className="bf-p">
          A Platform gets its blueprint when it is registered. Select a platform
          node to add resources.
        </p>
      )}

      {selection?.kind === "platform" && (
        <>
          <p className="bf-p" style={{ margin: "0 0 8px" }}>
            <strong>{selection.displayName}</strong>
            <br />
            <code className="bf-small">{selection.platformSlug}</code>
          </p>
          {showInspect && (
            <LinkedPlatforms platformSlug={selection.platformSlug} />
          )}

          {showAdd && canEditBlueprint && (
            <>
              <h2 className="bf-h3" style={{ margin: "16px 0 8px" }}>
                Add a root resource type
              </h2>
              {renderAddForm(null)}
            </>
          )}
        </>
      )}

      {selection?.kind === "resource" && (
        <>
          <p className="bf-p" style={{ margin: 0 }}>
            <strong>{selection.typeName}</strong>{" "}
            {selection.isStatic ? (
              <Badge state="neutral">Static</Badge>
            ) : (
              <Badge state="chill">Dynamic</Badge>
            )}
          </p>
          <p className="bf-small" style={{ margin: "4px 0 0" }}>
            {selection.isStatic ? (
              selection.nameRegex && (
                <>
                  Path segment: <code>{literalValue(selection.nameRegex)}</code>
                </>
              )
            ) : selection.nameRegex ? (
              <>
                Path pattern: <code>{selection.nameRegex}</code>
              </>
            ) : (
              "Path pattern: any value allowed"
            )}
          </p>

          {showInspect &&
            (canEditBlueprint ? (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-end",
                  marginTop: 8,
                }}
              >
                <Input
                  label="Qualified name"
                  description="The key consumers use in [RequireAccess]."
                  style={{ flex: 1 }}
                  value={editQualifiedName}
                  onChange={(e) => setEditQualifiedName(e.target.value)}
                />
                <Button
                  variant="filled"
                  state={
                    busy ||
                    !editQualifiedName.trim() ||
                    editQualifiedName.trim() === (selection.qualifiedName ?? "")
                      ? "inactive"
                      : undefined
                  }
                  onClick={() =>
                    onUpdateUsage(selection.usageId, editQualifiedName.trim())
                  }
                >
                  Save
                </Button>
              </div>
            ) : (
              selection.qualifiedName && (
                <p className="bf-small" style={{ margin: 0 }}>
                  Qualified name: <code>{selection.qualifiedName}</code>
                </p>
              )
            ))}

          {showAdd && canEditBlueprint && (
            <>
              <h2 className="bf-h3" style={{ margin: "16px 0 8px" }}>
                Add a child type
              </h2>
              {renderAddForm(selection.usageId)}
            </>
          )}

          {showInspect && canEditBlueprint && (
            <div className="bp-drawer-danger">
              <Button
                variant="basic"
                state={busy ? "inactive" : "alert"}
                onClick={() => {
                  if (confirm("Delete this node? Children will cascade.")) {
                    onDeleteUsage(selection.usageId);
                  }
                }}
              >
                <Icon icon={faTrash} marginRight />
                Delete (cascades children)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );

  function renderAddForm(parentUsageId: string | null) {
    return (
      <Grid gap={12}>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            variant={addMode === "search" ? "filled" : "flat"}
            small
            onClick={() => setAddMode("search")}
          >
            Search catalog
          </Button>
          <Button
            variant={addMode === "create" ? "filled" : "flat"}
            small
            onClick={() => setAddMode("create")}
          >
            Create new type
          </Button>
        </div>

        {addMode === "search" && (
          <>
            <Select
              label="Type"
              placeholder="Select a type…"
              options={typeOptions}
              value={selectedType}
              onChange={(opt) => {
                const newId = (opt as { value: string } | null)?.value ?? "";
                setTypeId(newId);
                setQnTouched(false);
              }}
              isClearable
            />
            {typeId && selectedTypeObj && (
              <p className="bf-small" style={{ margin: 0 }}>
                {selectedTypeObj.isStatic ? (
                  <Badge state="neutral">Static</Badge>
                ) : (
                  <Badge state="chill">Dynamic</Badge>
                )}{" "}
                → <code>{qualifiedName || "…"}</code>
              </p>
            )}
          </>
        )}

        {addMode === "create" && (
          <>
            <Input
              label="Display name"
              placeholder="e.g. Server"
              value={newDisplayName}
              onChange={(e) => {
                setNewDisplayName(e.target.value);
                if (!newSlugTouched) setNewSlug(slugify(e.target.value));
              }}
              required
            />
            <Input
              label="Slug"
              placeholder="e.g. server"
              value={newSlug}
              onChange={(e) => {
                setNewSlug(e.target.value);
                setNewSlugTouched(true);
              }}
              required
            />
            <fieldset
              className="bf-fieldset"
              style={{ margin: 0, padding: 0, border: "none" }}
            >
              <legend className="bf-label">Static or dynamic?</legend>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <RadioCard
                  name={`cardinality-${parentUsageId ?? "root"}`}
                  label="Static"
                  checked={newCardinality === "single"}
                  onChange={() => setNewCardinality("single")}
                >
                  <span
                    style={{
                      fontSize: "0.875em",
                      color: "var(--bfc-base-c-2)",
                    }}
                  >
                    A single fixed node. The path segment will be the slug
                    itself.
                  </span>
                </RadioCard>
                <RadioCard
                  name={`cardinality-${parentUsageId ?? "root"}`}
                  label="Dynamic"
                  checked={newCardinality === "many"}
                  onChange={() => setNewCardinality("many")}
                >
                  <span
                    style={{
                      fontSize: "0.875em",
                      color: "var(--bfc-base-c-2)",
                    }}
                  >
                    Many instances, each identified by a unique value (GUID,
                    slug, etc.).
                  </span>
                </RadioCard>
              </div>
            </fieldset>
            {newCardinality === "many" && (
              <>
                <Input
                  label="Variable name"
                  placeholder="e.g. serverId"
                  value={newPlaceholder}
                  onChange={(e) => setNewPlaceholder(e.target.value)}
                  feedback={
                    newPlaceholder.trim() &&
                    !isValidPlaceholder(newPlaceholder.trim())
                      ? "Letters, digits and underscore; cannot start with a digit"
                      : undefined
                  }
                  state={
                    newPlaceholder.trim() &&
                    !isValidPlaceholder(newPlaceholder.trim())
                      ? "alert"
                      : undefined
                  }
                />
                <div>
                  <span className="bf-label">ID format</span>
                  <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                    {(Object.keys(ID_PATTERNS) as IdPatternKey[]).map((key) => (
                      <Tooltip key={key} content={ID_PATTERNS[key].description}>
                        <Button
                          small
                          variant={newIdPattern === key ? "filled" : "flat"}
                          onClick={() => setNewIdPattern(key)}
                        >
                          {ID_PATTERNS[key].label}
                        </Button>
                      </Tooltip>
                    ))}
                  </div>
                  {newIdPattern === "custom" && (
                    <Input
                      label="Custom pattern (without anchors or prefix)"
                      placeholder="e.g. [a-z0-9]{6}"
                      value={newCustomPattern}
                      onChange={(e) => setNewCustomPattern(e.target.value)}
                      style={{ marginTop: 8 }}
                      required
                    />
                  )}
                </div>
                <Input
                  label="Path prefix"
                  description="Highly recommended. Distinguishes this type from siblings sharing the same ID format (e.g. srv- vs comp-)."
                  placeholder="e.g. srv-"
                  value={newPrefix}
                  onChange={(e) => setNewPrefix(e.target.value)}
                />
              </>
            )}
            {newCardinality === "many" && generatedRegex && (
              <p className="bf-small" style={{ margin: 0 }}>
                Path segment: <code>{newPreviewSegment ?? generatedRegex}</code>
              </p>
            )}
          </>
        )}

        <Input
          label="Qualified name"
          description="Developers will use this key to reference the resource path in code."
          value={qualifiedName}
          onChange={(e) => {
            setQualifiedName(e.target.value);
            setQnTouched(true);
          }}
        />

        <Button
          variant="filled"
          state={
            addMode === "search"
              ? canAddFromCatalog
                ? undefined
                : "inactive"
              : canAddFromCreate
                ? undefined
                : "inactive"
          }
          onClick={
            addMode === "search" ? handleAddFromCatalog : handleAddFromCreate
          }
        >
          Add to blueprint
          <Icon icon={faArrowRight} marginLeft />
        </Button>
      </Grid>
    );
  }
}
